// Approximated distribution slider (ACT2-531) — the sibling of NumericDistribution, drawn from a
// quantile ladder instead of a measured histogram. The shape is an EQUAL-AREA density: each band's
// AREA equals its population share, so `width ∝ value range` and `height ∝ share ÷ width`. A narrow
// band is a dense region, a wide band a sparse one.
//
// Interaction, axis and scale come from the shared shell (`SliderShell` + `useThresholdSlider`). What
// stays here is the BODY — the bands — and the share estimator. The body is the only part that knows
// this is drawn as bars at all: replacing it with a density curve or area is a change to this file's
// `children` and nothing else.
//
// The invariant: the region the plot highlights and the percentage the readout quotes are ONE region,
// computed once. The handle position(s) feed BOTH `slider.segments` (the shaded span) and the
// estimator's `selectedShare` — never two independent derivations that could drift.

import { useMemo } from 'react';

import { cn } from '@/_shared/lib/utils';

import type { ConditionValue } from '../types';
import { useThresholdSlider } from './hooks/useThresholdSlider';
import { InsightSummary } from './InsightSummary';
import { isTotalEstimated } from './lib/coverage-text';
import type { DistributionDomain } from './lib/distribution-domains';
import type { QuantileKnot, ShareOperator } from './lib/share-estimate';
import { buildKnots, selectedShare, shareOfAll } from './lib/share-estimate';
import { BAND_MIN_BAR_PCT, densityBarBoxes } from './lib/slider-geometry';
import { CONTINUOUS_OPS } from './lib/slider-ops';
import { SliderShell, SpanFill } from './SliderShell';

/** Levels worth a label, most informative first — a knot spanning several claims the highest-ranked. */
const LABELLED_LEVELS = [0.5, 0.95, 0.99, 0.75, 0.05, 0.25, 0.01];

/** The reader-facing name for a percentile. Plain English, never `p25` — see `axis-labels.ts`'s RANK. */
const LEVEL_NAMES: Record<string, string> = {
  '0.01': 'Bottom 1%',
  '0.05': 'Bottom 5%',
  '0.25': 'Bottom 25%',
  '0.5': 'Median',
  '0.75': 'Top 25%',
  '0.95': 'Top 5%',
  '0.99': 'Top 1%',
};
// No fallback. The default used to be `'Median'`, which is not a safe guess but a WRONG LABEL: the only
// caller that can reach an unlisted level is `endName`, reading a knot's own `q`/`leftQ` off a ladder
// whose levels come from `PROFILE_QUANTILES` — add a level there (p10, say) and every axis end landing
// on it would have claimed to be the median. Returning nothing lets the caller draw a bare value, which
// says less and asserts nothing false.
const levelName = (q: number): string | undefined => LEVEL_NAMES[String(q)];

/**
 * What an axis end IS: the column's extreme when the ladder was anchored there, otherwise the interior
 * quantile that ended up at the edge.
 *
 * Read off the level facing OUTWARD — `leftQ` at the low end, `q` at the high one — because a repeated
 * value coalesces into one knot whose `q` is the TOP of the run. A column that is half zeros collapses
 * min/p01/p05/p25/p50 into a single knot at 0 with `q = 0.5`, and naming it from `q` labelled the left
 * edge "p50": an assertion that half the population lies left of the chart's left edge, when 0 is the
 * minimum. `leftQ` is the field that proves otherwise, and it is 0.
 */
function endName(knot: QuantileKnot, atStart: boolean): string | undefined {
  const level = atStart ? knot.leftQ : knot.q;
  if (atStart && level === 0) return 'Lowest';
  if (!atStart && level === 1) return 'Highest';
  return levelName(level);
}

/**
 * The landmark name for an interior knot, or nothing when it marks no level worth labelling.
 *
 * A knot COVERS every level between its left limit and its right-continuous level — for a lone knot that
 * is the single level it was probed at, and exact matching worked. For a coalesced plateau it is the
 * whole run, and exact matching found nothing: a column with a third of its rows at one value spanning
 * p50–p75 produced a knot at `q = 0.75`, so `q === 0.5` matched nowhere and the median label vanished
 * from a position it could perfectly well be drawn at — the ACT2-534 §1 complaint returning by another
 * route. Ranking cannot protect a label that is never offered.
 */
function landmarkName(knot: QuantileKnot): string | undefined {
  const level = LABELLED_LEVELS.find((l) => knot.leftQ <= l && l <= knot.q);
  if (level === undefined) return undefined;
  return levelName(level);
}

/** The value at a given quantile level, read off the ladder by nearest knot — a sensible parked default
 *  for an unset handle, never a committed value. */
function valueAtQuantile(knots: readonly QuantileKnot[], q: number): number {
  let best = knots[0]!;
  for (const knot of knots) {
    if (Math.abs(knot.q - q) < Math.abs(best.q - q)) best = knot;
  }
  return best.value;
}

export function ApproxDistribution({
  quantiles,
  min,
  max,
  nonNullFraction,
  step,
  total,
  totalAccuracy,
  domain,
  operator,
  value,
  onPatch,
  ariaLabel,
  disabled = false,
}: {
  quantiles: { q: number; value: number }[];
  min?: number;
  max?: number;
  nonNullFraction?: number;
  /** Agent-chosen drag step; absent ⇒ the nice-number default from `defaultDragStep`. */
  step?: number;
  total?: number;
  /** The catalog's confidence in `total`. The row builder stamps ONE qualifier onto every control that
   *  publishes the same row count, so a `catalog_estimate` must not read as measured here while the pill
   *  control beside it discloses it. */
  totalAccuracy?: string;
  domain: DistributionDomain; // reads/writes the predicate value, formats labels, maps the operator
  operator: string;
  value: ConditionValue;
  onPatch: (patch: ConditionValue) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  // Assembled once per ladder: min/max become the q0/q1 anchors, non-monotonic knots are dropped.
  const knots = useMemo(() => buildKnots(quantiles, min, max), [quantiles, min, max]);

  const first = knots[0];
  const last = knots.at(-1);
  // The REAL range — never floored to 1, which drew a sub-1 column with its max at ~51% of the track.
  // Fallbacks keep the hook call unconditional; the under-two-knot case returns below.
  const lo0 = first?.value ?? 0;
  const hi0 = last?.value ?? 1;

  const slider = useThresholdSlider({
    min: lo0,
    max: hi0,
    step,
    integerValued: knots.every((knot) => Number.isInteger(knot.value)),
    fallback: {
      single: knots.length > 0 ? valueAtQuantile(knots, 0.5) : lo0,
      lo: knots.length > 0 ? valueAtQuantile(knots, 0.25) : lo0,
      hi: knots.length > 0 ? valueAtQuantile(knots, 0.75) : hi0,
    },
    domain,
    operator,
    value,
    onPatch,
    ariaLabel,
    disabled,
  });

  if (!first || !last) return null; // < 2 usable knots ⇒ no control (honesty rule)

  const { committed, hi, lo, op, pct, segments, single } = slider;

  // Equal-AREA bands: the ladder gives each one an equal-ish share over a very unequal width, so mass
  // here is the quantile delta and the shared density rule turns it into a height.
  const bands = knots.slice(0, -1).map((knot, i) => {
    const next = knots[i + 1]!;
    return { from: knot.value, to: next.value, mass: next.q - knot.q };
  });
  const boxes = densityBarBoxes(bands, pct, BAND_MIN_BAR_PCT);
  const bars = (fill: string) =>
    bands.map((_, i) => <div key={i} className={cn('absolute bottom-0 rounded-sm', fill)} style={boxes[i]} />);

  // The SAME region the plot shades — never a second derivation (the invariant). Gated on real anchors:
  // without both extrema the ladder's first/last knots are only interior quantiles, so treating them as
  // the whole domain would clamp a stored threshold into the interquartile range and quote a share for it.
  // A partial ladder still draws its bands and landmarks; it just goes quiet on the number.
  const anchored = typeof min === 'number' && Number.isFinite(min) && typeof max === 'number' && Number.isFinite(max);
  const shareOfNonNull =
    committed && anchored && CONTINUOUS_OPS.has(op)
      ? selectedShare(knots, op as ShareOperator, { single, lo, hi })
      : null;
  // Share of ALL entities when the fill rate is known — via `shareOfAll`, which returns null when it is
  // not, so the absolute COUNT is withheld rather than computed against a mismatched denominator (a
  // non-null share times the all-rows total overstates it by 1/fillRate). The percentage still falls back
  // to the non-null share (disclosed in the caption) so the readout survives an unknown row count.
  const shareAll = shareOfNonNull === null ? null : shareOfAll(shareOfNonNull, nonNullFraction);
  const share = shareAll ?? shareOfNonNull;
  const denom = typeof total === 'number' && Number.isFinite(total) ? total : undefined;
  const matchCount = shareAll !== null && denom !== undefined ? Math.round(shareAll * denom) : undefined;
  const sharePct = share !== null ? share * 100 : undefined;

  // The fill rate is a COLUMN property — constant across the selection — so its footnote is NOT this
  // component's to render: `InsightPanel` captions every control with one shared line (`coverage-text.ts`),
  // and a copy here would either duplicate that line or drift from it. `nonNullFraction` is still read
  // above, for the share-of-all ARITHMETIC, which is this component's own job.

  // Every knot is marked on the axis as a tick, and every INTERIOR knot is offered as a label — the
  // first and last are the axis ends, already labelled as the scale's anchors. `placeAxisLabels` takes
  // as many as genuinely fit, most informative first, so a roomy axis shows the whole ladder and a
  // cramped one still shows the median.
  //
  // Offering the whole ladder is what fixes ACT2-534 §2: the old hardcoded `q === 0.5 || q === 0.95`
  // was written when p95 was the last interior boundary, so labelling it meant "here is the top end".
  // #636 added p01/p99 and moved that goalpost, leaving the real tail boundary — where the top 1%
  // begins, the reason the tail quantiles were probed at all — with a tick and no value.
  const landmarks = knots
    .slice(1, -1)
    .map((knot) => ({ knot, label: landmarkName(knot) }))
    .filter((entry): entry is { knot: QuantileKnot; label: string } => entry.label !== undefined)
    .map(({ knot, label }) => ({ pos: pct(knot.value), label, value: domain.format(knot.value) }));

  return (
    <SliderShell
      slider={slider}
      summary={
        <InsightSummary
          matchCount={matchCount}
          total={denom}
          sharePct={sharePct}
          totalEstimated={isTotalEstimated(denom, totalAccuracy)}
          unset={!committed}
          unsetLabel="No threshold set"
          estimated
        />
      }
      ticks={knots.map((knot) => pct(knot.value))}
      landmarks={landmarks}
      minLabel={{ value: domain.format(lo0), name: endName(first, true) }}
      maxLabel={{ value: domain.format(hi0), name: endName(last, false) }}
    >
      {/* Bands positioned by VALUE (unequal width) — so they line up with the value-positioned handle. */}
      {bands.map((band, i) => (
        <div
          key={i}
          title={`${domain.format(band.from)}–${domain.format(band.to)}`}
          className="absolute bottom-0 rounded-sm bg-grey-100"
          style={boxes[i]}
        />
      ))}
      {/* Primary fill, clipped to the selected span(s) so a boundary band is coloured only up to the
          handle and the highlighted area tracks the interpolated share. */}
      {committed && <SpanFill spans={segments}>{bars('bg-primary')}</SpanFill>}
    </SliderShell>
  );
}
